import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { UserDetailsDto } from '../types/UserDetailsDto';

interface AuthContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: UserDetailsDto | null;
    login: (username: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    register: (username: string, email: string, password: string) => Promise<void>;
    getToken: () => string | null;
    fetchUserDetails: () => Promise<void>;
    setToken: (token: string) => void;
    updateUser: (user: UserDetailsDto) => void;
    expiredAccount: { message: string; renewalUrl: string } | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface ErrorResponse {
    status: number;
    message: string;
    renewalUrl?: string;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState<UserDetailsDto | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [expiredAccount, setExpiredAccount] = useState<{ message: string; renewalUrl: string } | null>(null);
    const navigate = useNavigate();
    const location = useLocation();

    const publicRoutes = [
        '/login',
        '/register',
        '/forgot-password',
        '/reset-password',
        '/verify'
    ];

    const fetchUserDetails = async () => {
        try {
            const response = await api.get('/auth/user');
            setUser(response.data);
        } catch (error) {
            console.error('Error fetching user details:', error);
        }
    };

    useEffect(() => {
        // Only fetch user details if on a protected route
        const token = localStorage.getItem('token');
        if (token && !publicRoutes.includes(location.pathname)) {
            setIsAuthenticated(true);
            setToken(token);
            fetchUserDetails();
        }
        setIsLoading(false);
    }, [location.pathname]);

    const login = async (username: string, password: string) => {
        try {
            setIsLoading(true);
            const response = await api.post('/auth/login', { username, password });
            handleSetToken(response.data.token);
            await fetchUserDetails();
            navigate('/');
        } catch (error: any) {
            if (error.response?.status === 403 && error.response?.data?.renewalUrl) {
                setExpiredAccount({
                    message: error.response.data.message || 'Your license has expired.',
                    renewalUrl: error.response.data.renewalUrl
                });
                return;
            }
            console.error('Login failed:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = async () => {
        try {
            setIsLoading(true);
            await api.post('/auth/logout');
            setToken(null);
            setIsAuthenticated(false);
            setUser(null);
            localStorage.removeItem('token');
            navigate('/login');
        } catch (error) {
            console.error('Logout failed:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const register = async (username: string, email: string, password: string) => {
        try {
            setIsLoading(true);
            await api.post('/auth/register', { username, email, password });
            navigate('/login');
        } catch (error) {
            console.error('Registration failed:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const getToken = () => {
        return localStorage.getItem('token');
    };

    const handleSetToken = (newToken: string) => {
        console.log('handleSetToken called with:', newToken);
        setToken(newToken);
        setIsAuthenticated(true);
        localStorage.setItem('token', newToken);
    };

    const updateUser = (updatedUser: UserDetailsDto) => {
        setUser(updatedUser);
    };

    return (
        <AuthContext.Provider value={{
            isAuthenticated,
            isLoading,
            user,
            login,
            logout,
            register,
            getToken,
            fetchUserDetails,
            setToken: handleSetToken,
            updateUser,
            expiredAccount,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};